import { SavedSearch } from '@creditsights/api-ui-web';
import {
  Button,
  ButtonMode,
  Icon,
  ICONS,
  Loader,
  Table,
  Tooltip,
  ToasterMode,
  DataLoadError,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SELECTED_SAVED_SEARCH_URL_KEY } from '../../../components/SavedSearchesDropdown/SavedSearchesDropdown.constants';
import useFetchSavedSearches from '../../../hooks/searches/useFetchSavedSearches';
import { getUrlSearchParams } from '../../SearchResults/SearchResults.logic';
import NoContent from '../NoContent/NoContent';
import { searchesColumnsConfig } from './SearchesTab.logic';
import { useGlobalModalContext } from '../../../components/GlobalModal';
import { useToast } from '../../../hooks/toast/useToast';
import { CheckboxChangeEvent } from '../../../components/formatters/CheckboxCellRenderer/CheckboxCellRenderer.types';
import { MODAL_TYPES } from '../../../components/GlobalModal/GlobalModal';
import { GENERIC_ERROR_TEXT, ROUTES } from '../../../Base.constants';
import useDeleteSavedSearch from '../../../hooks/searches/useDeleteSavedSearch';
import styles from './SearchesTab.module.scss';

const cx = classNames.bind(styles);

const getKey = (savedSearch: SavedSearch) => savedSearch.id;

const SearchesTab = (): React.ReactElement => {
  const { data: savedSearches, isLoading, isError, refetch } = useFetchSavedSearches();
  const { mutateAsync: deleteSavedSearch } = useDeleteSavedSearch();
  const [searchesToDelete, setSearchesToDelete] = useState<string[]>([]);
  const { showModal, closeModal, setError } = useGlobalModalContext();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const searchIds = useMemo<string[]>(() => savedSearches?.map((search) => search.id) ?? [], [savedSearches]);

  const onDeleteSuccess = () => {
    closeModal();

    const selectedSearch =
      searchesToDelete.length === 1 ? savedSearches?.find((s) => s.id === searchesToDelete[0]) : undefined;

    showToast(
      searchesToDelete.length > 1 ? (
        'Your Saved Searches have been removed'
      ) : (
        <span>
          Your Saved Search <strong>{selectedSearch!.name}</strong> has been removed
        </span>
      )
    );

    setSearchesToDelete([]);
  };

  const onEdit = (index: number, item: SavedSearch) => {
    const params = getUrlSearchParams(item);
    params.set(SELECTED_SAVED_SEARCH_URL_KEY, item.id);
    showToast(
      <span>
        We’ve taken you to the search results to edit your saved search named <strong>{item.name}</strong>
      </span>,
      ToasterMode.INFO
    );
    navigate(`${ROUTES.search}?${params}`);
  };

  const onSearchSelected = ({ checked, indeterminate, item }: CheckboxChangeEvent) => {
    if (!savedSearches) {
      return;
    }
    const search = item as SavedSearch;
    let idsToDelete: string[];
    if (indeterminate) {
      idsToDelete = searchIds;
    } else if (checked) {
      idsToDelete = search ? [...searchesToDelete, search.id] : searchIds;
    } else {
      idsToDelete = search ? searchesToDelete.filter((issuerToRemove) => issuerToRemove !== search.id) : [];
    }

    setSearchesToDelete(idsToDelete);
  };

  const checkboxCellProps = {
    onClick: onSearchSelected,
    getId: (search: SavedSearch) => search?.id,
    getIsChecked: (search: SavedSearch) => (!search ? !!searchesToDelete.length : searchesToDelete.includes(search.id)),
    getIsIndeterminate: (search) =>
      search ? false : Boolean(searchesToDelete.length && searchesToDelete.length < (savedSearches?.length ?? 0)),
  };

  const onDeleteSearches = useCallback(() => {
    showModal(MODAL_TYPES.CONFIRM_DELETE_SEARCHES, {
      selectedSearchesCount: searchesToDelete.length,
      onConfirm: async () => {
        const deleteRequests = searchesToDelete.map((id) => deleteSavedSearch(id));
        try {
          await Promise.all(deleteRequests);
          onDeleteSuccess();
        } catch (errors) {
          setError(GENERIC_ERROR_TEXT);
        }
      },
    });
  }, [searchesToDelete, showModal, searchesToDelete]);

  const deleteCount = searchesToDelete.length > 0 ? `(${searchesToDelete.length})` : '';

  const renderContent = () => {
    if (isError) {
      return <DataLoadError onRetryClick={refetch} />;
    }

    if (!savedSearches || !savedSearches.length) {
      return (
        <NoContent
          dataTestId="no-searches-saved"
          header="No Saved Searches"
          infoMessage="You’ll see your saved searches here after saving at least one search result. "
          iconName={ICONS.NoContent}
        />
      );
    }
    return (
      <div className={cx('table-wrapper')}>
        <Table
          data={savedSearches}
          autoLayout
          tableViewConfig={searchesColumnsConfig(cx('cell'), checkboxCellProps, onEdit)}
          getKey={getKey}
        />
      </div>
    );
  };

  return (
    <div className={cx('body')} data-testid="saved-searches">
      <div className={cx('row')}>
        <h1 className={cx('header')}>Searches</h1>
        {!!savedSearches?.length && (
          <Tooltip
            disabled={!!searchesToDelete.length}
            content="Select Saved Searches to delete"
            data-testid="delete-searches-tooltip"
          >
            <div>
              <Button
                ariaLabel={`Delete Selected ${deleteCount}`}
                disabled={!searchesToDelete.length}
                mode={ButtonMode.TERTIARY}
                onClick={onDeleteSearches}
              >
                <Icon name={ICONS.Delete} />
                Delete Selected {deleteCount}
              </Button>
            </div>
          </Tooltip>
        )}
      </div>
      {isLoading ? <Loader position="center" /> : renderContent()}
    </div>
  );
};

export default SearchesTab;
